import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/esm/objectWithoutPropertiesLoose';
import _extends from '@babel/runtime/helpers/esm/extends';
import { useReducer, useRef, useEffect } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { getKey } from 'keyboard-key';
import { useId } from '@reach/auto-id';
import { checkPropTypes, array, func, bool, number, any, string } from 'prop-types';

var statusDiv = typeof document === 'undefined' ? null : document.getElementById('downshift-a11y-status-message');
var cleanupTimerID;

function getAriaLiveMessageContainer() {
  if (statusDiv) {
    return statusDiv;
  }

  statusDiv = document.createElement('div');
  statusDiv.setAttribute('id', 'a11y-status-message');
  statusDiv.setAttribute('role', 'status');
  statusDiv.setAttribute('aria-live', 'polite');
  statusDiv.setAttribute('aria-relevant', 'additions text');
  Object.assign(statusDiv.style, {
    border: '0',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    width: '1px'
  });
  document.body.appendChild(statusDiv);
  return statusDiv;
}

function setAriaLiveMessage(message) {
  var div = getAriaLiveMessageContainer();

  if (!message) {
    return;
  }

  if (cleanupTimerID) {
    clearTimeout(cleanupTimerID);
    cleanupTimerID = null;
  }

  div.textContent = message;
  cleanupTimerID = setTimeout(function () {
    div.textContent = '';
    cleanupTimerID = null;
  }, 500);
}

function getDefaultIds(uniqueId) {
  return {
    label: "downshift-label-" + uniqueId,
    menu: "downshift-menu-" + uniqueId,
    item: function item(index) {
      return "downshift-item-" + uniqueId + "-" + index;
    },
    toggleButton: "downshift-toggle-button-" + uniqueId
  };
}
/**
 * This is intended to be used to compose event handlers.
 * They are executed in order until one of them sets
 * `event.preventDownshiftDefault = true`.
 * @param {...Function} fns the event handler functions
 * @return {Function} the event handler to add to an element
 */


function callAllEventHandlers() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }

  return function (event) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    return fns.some(function (fn) {
      if (fn) {
        fn.apply(void 0, [event].concat(args));
      }

      return event.preventDownshiftDefault || Object.prototype.hasOwnProperty.call(event, 'nativeEvent') && event.nativeEvent.preventDownshiftDefault;
    });
  };
}
/**
 * This return a function that will call all the given functions with
 * the arguments with which it's called. It does a null-check before
 * attempting to call the functions and can take any number of functions.
 * @param {...Function} fns the functions to call
 * @return {Function} the function that calls all the functions
 */


function callAll() {
  for (var _len3 = arguments.length, fns = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    fns[_key3] = arguments[_key3];
  }

  return function () {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }

    fns.forEach(function (fn) {
      if (fn) {
        fn.apply(void 0, args);
      }
    });
  };
}

function getNextWrappingIndex(moveAmount, baseIndex, itemsLength, circular) {
  if (baseIndex === -1) {
    return moveAmount > 0 ? 0 : itemsLength - 1;
  }

  var nextIndex = baseIndex + moveAmount;

  if (nextIndex < 0) {
    return circular ? itemsLength - 1 : 0;
  }

  if (nextIndex >= itemsLength) {
    return circular ? 0 : itemsLength - 1;
  }

  return nextIndex;
}

function getItemIndexByCharacterKey(keysSoFar, highlightedIndex, items, itemToString) {
  var newHighlightedIndex = -1;
  var itemStrings = items.map(function (item) {
    return itemToString(item).toLowerCase();
  });

  if (highlightedIndex > -1) {
    var startPosition = highlightedIndex + (keysSoFar > 1 ? 0 : 1);
    newHighlightedIndex = itemStrings.slice(startPosition).findIndex(function (itemString) {
      return itemString.startsWith(keysSoFar);
    });

    if (newHighlightedIndex > -1) {
      return newHighlightedIndex + startPosition;
    }
  }

  if (newHighlightedIndex === -1) {
    newHighlightedIndex = itemStrings.findIndex(function (itemString) {
      return itemString.startsWith(keysSoFar);
    });
  }

  return newHighlightedIndex;
}

function getState(state, props) {
  return Object.keys(state).reduce(function (prevState, key) {
    // eslint-disable-next-line no-param-reassign
    prevState[key] = props[key] === undefined ? state[key] : props[key];
    return prevState;
  }, {});
}

function getItemIndex(index, item, items) {
  if (index !== undefined) {
    return index;
  }

  if (items.length === 0) {
    return -1;
  }

  return items.indexOf(item);
}

function getPropTypesValidator(caller, propTypes) {
  return function (options) {
    if (options === void 0) {
      options = {};
    }

    Object.entries(propTypes).forEach(function (_ref) {
      var key = _ref[0];
      checkPropTypes(propTypes, options, key, caller.name);
    });
  };
}

var actionTypes = {
  MenuKeyDownArrowDown: 'MenuKeyDownArrowDown',
  MenuKeyDownArrowUp: 'MenuKeyDownArrowUp',
  MenuKeyDownEscape: 'MenuKeyDownEscape',
  MenuKeyDownHome: 'MenuKeyDownHome',
  MenuKeyDownEnd: 'MenuKeyDownEnd',
  MenuKeyDownEnter: 'MenuKeyDownEnter',
  MenuKeyDownCharacter: 'MenuKeyDownCharacter',
  MenuBlur: 'MenuBlur',
  ItemHover: 'ItemHover',
  ItemClick: 'ItemClick',
  ToggleButtonKeyDownArrowDown: 'ToggleButtonKeyDownArrowDown',
  ToggleButtonKeyDownArrowUp: 'ToggleButtonKeyDownArrowUp',
  ToggleButtonClick: 'ToggleButtonClick',
  FunctionToggleMenu: 'FunctionToggleMenu',
  FunctionOpenMenu: 'FunctionOpenMenu',
  FunctionCloseMenu: 'FunctionCloseMenu',
  FunctionSetHighlightedIndex: 'FunctionSetHighlightedIndex',
  FunctionSetSelectedItem: 'FunctionSetSelectedItem',
  FunctionClearKeysSoFar: 'FunctionClearKeysSoFar',
  FunctionReset: 'FunctionReset'
};
var defaultStateValues = {
  highlightedIndex: -1,
  isOpen: false,
  selectedItem: null
};

function getA11yStatusMessage(_ref) {
  var isOpen = _ref.isOpen,
      selectedItem = _ref.selectedItem,
      items = _ref.items,
      itemToString = _ref.itemToString;

  if (selectedItem) {
    return itemToString(selectedItem) + " has been selected.";
  }

  if (!items) {
    return '';
  }

  var resultCount = items.length;

  if (isOpen) {
    if (resultCount === 0) {
      return 'No results are available';
    }

    return resultCount + " result" + (resultCount === 1 ? ' is' : 's are') + " available, use up and down arrow keys to navigate. Press Enter key to select.";
  }

  return '';
}

var getHighlightedIndexOnOpen = function (props, state, offset) {
  var items = props.items,
      initialHighlightedIndex = props.initialHighlightedIndex,
      defaultHighlightedIndex = props.defaultHighlightedIndex;
  var selectedItem = state.selectedItem,
      highlightedIndex = state.highlightedIndex; // initialHighlightedIndex will give value to highlightedIndex on initial state only.

  if (initialHighlightedIndex && highlightedIndex > -1) {
    return initialHighlightedIndex;
  }

  if (defaultHighlightedIndex) {
    return defaultHighlightedIndex;
  }

  if (selectedItem) {
    if (offset === 0) {
      return items.indexOf(selectedItem);
    }

    return getNextWrappingIndex(offset, items.indexOf(selectedItem), items.length, false);
  }

  if (offset === 0) {
    return -1;
  }

  return offset < 0 ? items.length - 1 : 0;
};

var getInitialValue = function (props, propKey) {
  if (props[propKey] !== undefined) {
    return props[propKey];
  }

  var initialPropKey = "initial" + propKey.slice(0, 1).toUpperCase() + propKey.slice(1);

  if (props[initialPropKey] !== undefined) {
    return props[initialPropKey];
  }

  var defaultPropKey = "default" + propKey.slice(0, 1).toUpperCase() + propKey.slice(1);

  if (props[defaultPropKey] !== undefined) {
    return props[defaultPropKey];
  }

  return defaultStateValues[propKey];
};

var getInitialState = function (props) {
  return {
    highlightedIndex: getInitialValue(props, 'highlightedIndex'),
    isOpen: getInitialValue(props, 'isOpen'),
    selectedItem: getInitialValue(props, 'selectedItem'),
    keysSoFar: ''
  };
};

var propTypes = {
  items: array.isRequired,
  itemToString: func,
  getA11yStatusMessage: func,
  circularNavigation: bool,
  highlightedIndex: number,
  defaultHighlightedIndex: number,
  initialHighlightedIndex: number,
  isOpen: bool,
  defaultIsOpen: bool,
  initialsOpen: bool,
  selectedItem: any,
  initialSelectedItem: any,
  defaultSelectedItem: any,
  labelId: string,
  menuId: string,
  itemId: func,
  toggleButtonId: string,
  stateReducer: func,
  onSelectedItemChange: func,
  onHighlightedIndexChange: func,
  onStateChange: func,
  onIsOpenChange: func
};

function downshiftSelectionReducer(state, action) {
  var type = action.type,
      props = action.props,
      shiftKey = action.shiftKey;
  var changes;

  switch (type) {
    case actionTypes.ItemHover:
      changes = {
        highlightedIndex: action.index
      };
      break;

    case actionTypes.ItemClick:
      changes = {
        isOpen: false,
        highlightedIndex: -1,
        selectedItem: props.items[action.index]
      };
      break;

    case actionTypes.MenuBlur:
      changes = _extends({
        isOpen: false,
        highlightedIndex: -1
      }, state.highlightedIndex >= 0 && {
        selectedItem: props.items[state.highlightedIndex]
      });
      break;

    case actionTypes.MenuKeyDownArrowDown:
      changes = {
        highlightedIndex: getNextWrappingIndex(shiftKey ? 5 : 1, state.highlightedIndex, props.items.length, props.circularNavigation)
      };
      break;

    case actionTypes.MenuKeyDownArrowUp:
      changes = {
        highlightedIndex: getNextWrappingIndex(shiftKey ? -5 : -1, state.highlightedIndex, props.items.length, props.circularNavigation)
      };
      break;

    case actionTypes.MenuKeyDownHome:
      changes = {
        highlightedIndex: 0
      };
      break;

    case actionTypes.MenuKeyDownEnd:
      changes = {
        highlightedIndex: props.items.length - 1
      };
      break;

    case actionTypes.MenuKeyDownEscape:
      changes = {
        isOpen: false,
        highlightedIndex: -1
      };
      break;

    case actionTypes.MenuKeyDownEnter:
      changes = {
        isOpen: false,
        highlightedIndex: -1,
        selectedItem: props.items[state.highlightedIndex]
      };
      break;

    case actionTypes.MenuKeyDownCharacter:
      {
        var lowercasedKey = action.key;
        var keysSoFar = "" + state.keysSoFar + lowercasedKey;
        var highlightedIndex = getItemIndexByCharacterKey(keysSoFar, state.highlightedIndex, props.items, props.itemToString);
        changes = _extends({
          keysSoFar: keysSoFar
        }, highlightedIndex >= 0 && {
          highlightedIndex: highlightedIndex
        });
      }
      break;

    case actionTypes.ToggleButtonKeyDownArrowDown:
      {
        changes = {
          isOpen: true,
          highlightedIndex: getHighlightedIndexOnOpen(props, state, 1)
        };
        break;
      }

    case actionTypes.ToggleButtonKeyDownArrowUp:
      changes = {
        isOpen: true,
        highlightedIndex: getHighlightedIndexOnOpen(props, state, -1)
      };
      break;

    case actionTypes.ToggleButtonClick:
    case actionTypes.FunctionToggleMenu:
      changes = {
        isOpen: !state.isOpen,
        highlightedIndex: state.isOpen ? -1 : getHighlightedIndexOnOpen(props, state, 0)
      };
      break;

    case actionTypes.FunctionOpenMenu:
      changes = {
        isOpen: true,
        highlightedIndex: getHighlightedIndexOnOpen(props, state, 0)
      };
      break;

    case actionTypes.FunctionCloseMenu:
      changes = {
        isOpen: false
      };
      break;

    case actionTypes.FunctionSetHighlightedIndex:
      changes = {
        highlightedIndex: action.highlightedIndex
      };
      break;

    case actionTypes.FunctionSetSelectedItem:
      changes = {
        selectedItem: action.selectedItem
      };
      break;

    case actionTypes.FunctionClearKeysSoFar:
      changes = {
        keysSoFar: ''
      };
      break;

    case actionTypes.FunctionReset:
      changes = {
        highlightedIndex: props.defaultHighlightedIndex === undefined ? defaultStateValues.highlightedIndex : props.defaultHighlightedIndex,
        isOpen: props.defaultIsOpen === undefined ? defaultStateValues.isOpen : props.defaultIsOpen,
        selectedItem: props.defaultSelectedItem === undefined ? defaultStateValues.selectedItem : props.defaultSelectedItem
      };
      break;

    default:
      throw new Error('Reducer called without proper action type.');
  }

  if (props) {
    if (props.onIsOpenChange && changes.isOpen !== undefined) {
      props.onIsOpenChange(changes);
    }

    if (props.onHighlightedIndexChange && changes.highlightedIndex !== undefined) {
      props.onHighlightedIndexChange(changes);
    }

    if (props.onSelectedItemChange && changes.selectedItem !== undefined) {
      props.onSelectedItemChange(changes);
    }

    if (props.onStateChange && changes !== undefined) {
      props.onStateChange(changes);
    }
  }

  return _extends({}, state, {}, changes);
}

var keyClear = null;
var validateUseLocalStorageState = getPropTypesValidator(useDownshiftSelection, propTypes);

function useDownshiftSelection(userProps) {
  if (userProps === void 0) {
    userProps = {};
  }

  validateUseLocalStorageState(userProps); // Props defaults and destructuring.

  var props = _extends({
    itemToString: function (item) {
      return item ? String(item) : '';
    },
    stateReducer: function (s, a) {
      return a.changes;
    },
    getA11yStatusMessage: getA11yStatusMessage
  }, userProps);

  var items = props.items,
      itemToString = props.itemToString,
      getA11yStatusMessage$1 = props.getA11yStatusMessage,
      initialIsOpen = props.initialIsOpen,
      defaultIsOpen = props.defaultIsOpen,
      labelIdFromProps = props.labelId,
      menuIdFromProps = props.menuId,
      itemIdFromProps = props.itemId,
      toggleButtonIdFromProps = props.toggleButtonId,
      stateReducer = props.stateReducer;
  var defaultIds = getDefaultIds(useId()); // Initial state depending on controlled props.

  var initialState = getInitialState(props); // Reducer init.

  var _useReducer = useReducer(function (state, action) {
    var changes = downshiftSelectionReducer(state, action);
    return getState(stateReducer(state, _extends({}, action, {
      changes: changes
    })), props);
  }, initialState),
      _useReducer$ = _useReducer[0],
      isOpen = _useReducer$.isOpen,
      highlightedIndex = _useReducer$.highlightedIndex,
      selectedItem = _useReducer$.selectedItem,
      keysSoFar = _useReducer$.keysSoFar,
      dispatch = _useReducer[1]; // IDs generation.


  var labelId = labelIdFromProps || defaultIds.label;
  var itemId = itemIdFromProps || defaultIds.item;
  var menuId = menuIdFromProps || defaultIds.menu;
  var toggleButtonId = toggleButtonIdFromProps || defaultIds.toggleButton; // Refs

  var toggleButtonRef = useRef(null);
  var menuRef = useRef(null);
  var itemRefs = useRef();
  itemRefs.current = [];
  var isInitialMount = useRef(true); // Effects.
  // Status message on open.

  useEffect(function () {
    if (!isOpen) {
      return;
    }

    setAriaLiveMessage(getA11yStatusMessage$1({
      selectedItem: selectedItem,
      itemToString: itemToString,
      isOpen: isOpen,
      items: items
    })); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Status message on selection.

  useEffect(function () {
    if (!selectedItem) {
      return;
    }

    setAriaLiveMessage(getA11yStatusMessage$1({
      selectedItem: selectedItem,
      itemToString: itemToString,
      isOpen: isOpen,
      items: items
    })); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]); // Concatenates keysSoFar and schedules the cleanup.

  useEffect(function () {
    if (!keysSoFar) {
      return;
    }

    if (keyClear) {
      clearTimeout(keyClear);
      keyClear = null;
    }

    keyClear = setTimeout(function () {
      dispatch({
        type: actionTypes.FunctionClearKeysSoFar,
        props: props
      });
    }, 500); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysSoFar]);
  useEffect(function () {
    // Don't focus menu on first render, most probably is closed.
    if (isInitialMount.current) {
      isInitialMount.current = false; // But it was initialised as open, then focus.

      if (initialIsOpen || defaultIsOpen || isOpen) {
        menuRef.current.focus();
      }

      return;
    } // Focuses menu on open.


    if (isOpen) {
      menuRef.current.focus(); // Focuses toggleButton on close.
    } else if (document.activeElement === menuRef.current) {
      toggleButtonRef.current.focus();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [isOpen]); // Scrolls highlighted index into view.

  useEffect(function () {
    if (highlightedIndex < 0 || !isOpen || !itemRefs.current.length) {
      return;
    }

    scrollIntoView(itemRefs.current[highlightedIndex], {
      scrollMode: 'if-needed',
      block: 'nearest',
      inline: 'nearest'
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedIndex]); // Event handler functions

  var menuKeyDownHandlers = {
    ArrowDown: function ArrowDown(event) {
      event.preventDefault();
      dispatch({
        type: actionTypes.MenuKeyDownArrowDown,
        props: props,
        shiftKey: event.shiftKey
      });
    },
    ArrowUp: function ArrowUp(event) {
      event.preventDefault();
      dispatch({
        type: actionTypes.MenuKeyDownArrowUp,
        props: props,
        shiftKey: event.shiftKey
      });
    },
    Home: function Home(event) {
      event.preventDefault();
      dispatch({
        type: actionTypes.MenuKeyDownHome,
        props: props
      });
    },
    End: function End(event) {
      event.preventDefault();
      dispatch({
        type: actionTypes.MenuKeyDownEnd,
        props: props
      });
    },
    Escape: function Escape() {
      dispatch({
        type: actionTypes.MenuKeyDownEscape,
        props: props
      });
    },
    Enter: function Enter() {
      dispatch({
        type: actionTypes.MenuKeyDownEnter,
        props: props
      });
    },
    Tab: function Tab(event) {
      // The exception that calls MenuBlur.
      if (event.shiftKey) {
        dispatch({
          type: actionTypes.MenuBlur,
          props: props
        });
      }
    }
  };
  var toggleButtonKeyDownHandlers = {
    ArrowDown: function ArrowDown(event) {
      event.preventDefault();
      dispatch({
        type: actionTypes.ToggleButtonKeyDownArrowDown,
        props: props
      });
    },
    ArrowUp: function ArrowUp(event) {
      event.preventDefault();
      dispatch({
        type: actionTypes.ToggleButtonKeyDownArrowUp,
        props: props
      });
    }
  }; // Event handlers.

  var menuHandleKeyDown = function (event) {
    var key = getKey(event);

    if (key && menuKeyDownHandlers[key]) {
      menuKeyDownHandlers[key](event);
    } else if (/^\S{1}$/.test(key)) {
      dispatch({
        type: actionTypes.MenuKeyDownCharacter,
        key: key,
        props: props
      });
    }
  }; // Focus going back to the toggleButton is something we control (Escape, Enter, Click).
  // We are toggleing special actions for these cases in reducer, not MenuBlur.
  // Since Shift-Tab also lands focus on toggleButton, we will handle it as exception and call MenuBlur.


  var menuHandleBlur = function (event) {
    if (event.relatedTarget !== toggleButtonRef.current) {
      dispatch({
        type: actionTypes.MenuBlur,
        props: props
      });
    }
  };

  var toggleButtonHandleClick = function () {
    dispatch({
      type: actionTypes.ToggleButtonClick,
      props: props
    });
  };

  var toggleButtonHandleKeyDown = function (event) {
    var key = getKey(event);

    if (key && toggleButtonKeyDownHandlers[key]) {
      toggleButtonKeyDownHandlers[key](event);
    }
  };

  var itemHandleMouseOver = function (index) {
    dispatch({
      type: actionTypes.ItemHover,
      props: props,
      index: index
    });
  };

  var itemHandleClick = function (index) {
    dispatch({
      type: actionTypes.ItemClick,
      props: props,
      index: index
    });
  }; // returns


  return {
    // prop getters.
    getToggleButtonProps: function getToggleButtonProps(_temp2) {
      var _extends3;

      var _ref2 = _temp2 === void 0 ? {} : _temp2,
          onClick = _ref2.onClick,
          onKeyDown = _ref2.onKeyDown,
          _ref2$refKey = _ref2.refKey,
          refKey = _ref2$refKey === void 0 ? 'ref' : _ref2$refKey,
          ref = _ref2.ref,
          rest = _objectWithoutPropertiesLoose(_ref2, ["onClick", "onKeyDown", "refKey", "ref"]);

      return _extends((_extends3 = {}, _extends3[refKey] = callAll(ref, function (toggleButtonNode) {
        toggleButtonRef.current = toggleButtonNode;
      }), _extends3.id = toggleButtonId, _extends3['aria-haspopup'] = 'listbox', _extends3['aria-expanded'] = isOpen, _extends3['aria-labelledby'] = labelId + " " + toggleButtonId, _extends3.onClick = callAllEventHandlers(onClick, toggleButtonHandleClick), _extends3.onKeyDown = callAllEventHandlers(onKeyDown, toggleButtonHandleKeyDown), _extends3), rest);
    },
    getLabelProps: function getLabelProps() {
      return {
        id: labelId
      };
    },
    getMenuProps: function getMenuProps(_temp) {
      var _extends2;

      var _ref = _temp === void 0 ? {} : _temp,
          onKeyDown = _ref.onKeyDown,
          onBlur = _ref.onBlur,
          _ref$refKey = _ref.refKey,
          refKey = _ref$refKey === void 0 ? 'ref' : _ref$refKey,
          ref = _ref.ref;

      return _extends((_extends2 = {}, _extends2[refKey] = callAll(ref, function (menuNode) {
        menuRef.current = menuNode;
      }), _extends2.id = menuId, _extends2.role = 'listbox', _extends2['aria-labelledby'] = labelId, _extends2.tabIndex = -1, _extends2), highlightedIndex > -1 && {
        'aria-activedescendant': itemId(highlightedIndex)
      }, {
        onKeyDown: callAllEventHandlers(onKeyDown, menuHandleKeyDown),
        onBlur: callAllEventHandlers(onBlur, menuHandleBlur)
      });
    },
    getItemProps: function getItemProps(_temp3) {
      var _extends4;

      var _ref3 = _temp3 === void 0 ? {} : _temp3,
          item = _ref3.item,
          index = _ref3.index,
          _ref3$refKey = _ref3.refKey,
          refKey = _ref3$refKey === void 0 ? 'ref' : _ref3$refKey,
          ref = _ref3.ref,
          onMouseOver = _ref3.onMouseOver,
          onClick = _ref3.onClick,
          rest = _objectWithoutPropertiesLoose(_ref3, ["item", "index", "refKey", "ref", "onMouseOver", "onClick"]);

      var itemIndex = getItemIndex(index, item, items);

      if (itemIndex < 0) {
        throw new Error('Pass either item or item index in getItemProps!');
      }

      return _extends((_extends4 = {}, _extends4[refKey] = callAll(ref, function (itemNode) {
        if (itemNode) {
          itemRefs.current.push(itemNode);
        }
      }), _extends4.role = 'option', _extends4), itemIndex === highlightedIndex && {
        'aria-selected': true
      }, {
        id: itemId(itemIndex),
        onMouseOver: callAllEventHandlers(onMouseOver, function () {
          return itemHandleMouseOver(itemIndex);
        }),
        onClick: callAllEventHandlers(onClick, function () {
          return itemHandleClick(itemIndex);
        })
      }, rest);
    },
    // actions.
    toggleMenu: function toggleMenu() {
      dispatch({
        type: actionTypes.FunctionToggleMenu,
        props: props
      });
    },
    openMenu: function openMenu() {
      dispatch({
        type: actionTypes.FunctionOpenMenu,
        props: props
      });
    },
    closeMenu: function closeMenu() {
      dispatch({
        type: actionTypes.FunctionCloseMenu
      });
    },
    setHighlightedIndex: function setHighlightedIndex(newHighlightedIndex) {
      dispatch({
        type: actionTypes.FunctionSetHighlightedIndex,
        highlightedIndex: newHighlightedIndex
      });
    },
    setSelectedItem: function setSelectedItem(newSelectedItem) {
      dispatch({
        type: actionTypes.FunctionSetSelectedItem,
        selectedItem: newSelectedItem
      });
    },
    reset: function reset() {
      dispatch({
        type: actionTypes.FunctionReset,
        props: props
      });
    },
    // state.
    highlightedIndex: highlightedIndex,
    isOpen: isOpen,
    selectedItem: selectedItem,
    // props.
    items: items
  };
}

export { useDownshiftSelection };
